import { useState } from 'react';
import { RightOutlined, WarningOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Checkbox,
  Empty,
  Flex,
  Grid,
  Input,
  List,
  Modal,
  Pagination,
  Segmented,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableColumnType } from 'antd';
import { useNavigate } from 'react-router';
import { AREAS_DA_LEI, nomeDasAreas, ordemDaArea } from '@titularis/api-client';
import type {
  AreaDaLei,
  ArtigoComentado,
  ArtigoPendente,
  EstadoDoArtigo,
  LeiDoAdmin,
  OrdemDoBacklog,
  RecorteDoBacklog,
} from '@titularis/api-client';
import { useEstadoDeLista } from '@titularis/ui';
import { api } from '../../api';
import { formatarData } from '../../dominio';
import { paginacao } from '../../paginacao';
import { GavetaDoComentario, type ArtigoNaGaveta } from './GavetaDoComentario';
import { ROTULO, rotuloDoEstado } from './estados';
import {
  lerConsulta,
  opcoesDeEstado,
  paginaDoInventario,
  POR_PAGINA_PADRAO,
  resumoDoInventario,
  TAMANHOS_DE_PAGINA,
} from './listaDeComentarios';
import {
  lerOpcao,
  ORDEM_DA_LEI,
  ORDENS,
  ORDENS_DO_BACKLOG,
  RECORTES_DO_BACKLOG,
  VARREDURA_DA_LEI,
} from './ordens';

/**
 * Legislação Comentada por artigo (F14).
 *
 * Três perguntas numa tela só, porque são a mesma sequência de trabalho:
 * **o que está no ar** (as leis), **o que comentar em seguida** (o backlog, na
 * ordem da incidência) e **o que já se escreveu** (o inventário, em qualquer
 * estado).
 *
 * A ordem não é escolha desta tela: ela vem da incidência calculada sobre as
 * questões publicadas. A tela mostra o número ao lado de cada linha justamente
 * para a ordem não parecer arbitrária a quem a lê.
 */

/** O mesmo corte das demais telas do admin: abaixo de `md`, tabela vira lista. */
const useCompacto = () => !Grid.useBreakpoint().md;

/**
 * As larguras das colunas das três tabelas, que também são a conta da rolagem.
 *
 * **`scroll={{ x: true }}` deixa a tabela em `width: auto`**, e aí a coluna com
 * reticência - a única sem largura, e a que não quebra linha - estica a tabela
 * até o texto inteiro caber numa linha só. No inventário isso dava 6.300px de
 * tabela numa área de 1.150; no backlog, a ação sairia do campo de visão no dia
 * em que alguém escrevesse uma ressalva longa, e nas leis as duas chaves iriam
 * junto com um título comprido - que é o defeito que a tela da lei já tinha, e
 * é o que trouxe alguém aqui. Com um número a tabela para de crescer, o
 * `minWidth: 100%` do Antd a faz preencher a área, e a reticência volta a
 * cortar o texto onde deve.
 */
const LARGURA_DAS_LEIS = {
  lei: 190,
  questoes: 110,
  artigosCitados: 150,
  comentados: 130,
  publicada: 90,
  gratis: 90,
  area: 220,
  produtos: 170,
  seta: 40,
};

const LARGURA_DO_BACKLOG = { lei: 180, artigo: 110, questoes: 110, situacao: 130, acao: 150 };

/** A coluna das caixas de marcar, no padrão do Ant Design. */
const LARGURA_DA_SELECAO = 32;

const LARGURA_DO_INVENTARIO = {
  lei: 170,
  artigo: 110,
  situacao: 130,
  questoes: 100,
  autor: 180,
  data: 130,
};

/**
 * O piso das colunas sem largura fixa - a ressalva e a observação do motor.
 * Elas ficam com a sobra, que pode ser a tela toda; o piso é só o que entra na
 * conta da rolagem, para o texto não sumir na janela mais estreita que ainda
 * mostra a tabela.
 */
const PISO_DO_TEXTO = 240;

const somar = (larguras: Record<string, number>) =>
  Object.values(larguras).reduce((soma, largura) => soma + largura, 0);

/**
 * A coluna do artigo, e ela ordena.
 *
 * **É o primeiro lugar em que se tenta ordenar uma tabela**, e nas duas listas
 * daqui o cabeçalho não fazia nada - a queixa que trouxe alguém aqui. Ele escreve
 * a mesma `ordem` que o seletor ao lado, e não uma segunda ordenação: duas
 * ordens, uma da tabela e outra do servidor, discordariam já na segunda página.
 *
 * `sorter: true` é como o Antd diz "quem ordena é o servidor": ele desenha a
 * seta e avisa a mudança, sem tocar nas linhas. E `sortDirections` traz uma
 * direção só porque a ordem da lei é uma - do art. 1º para o fim. Do outro lado
 * do clique não está a ordem inversa, está a ordem padrão da aba.
 */
const colunaDoArtigo = <T extends { artigo: string }>(
  largura: number,
  ordem: string | undefined,
): TableColumnType<T> => ({
  title: 'Artigo',
  dataIndex: 'artigo',
  width: largura,
  sorter: true,
  sortDirections: ['ascend'],
  sortOrder: ordem === ORDEM_DA_LEI ? 'ascend' : null,
  render: (artigo: string) => <Typography.Text strong>art. {artigo}</Typography.Text>,
});

/** O que o clique no cabeçalho quis dizer: a ordem da lei, ou de volta ao padrão. */
const ordemDoCabecalho = (ordenacao: { order?: string | null } | { order?: string | null }[]) =>
  (Array.isArray(ordenacao) ? ordenacao[0] : ordenacao)?.order ? ORDEM_DA_LEI : undefined;

/** A primeira aba é o padrão, e padrão não ocupa espaço na barra de endereço. */
const ABA_PADRAO = 'leis';

export function ArtigosComentadosPage() {
  const compacto = useCompacto();
  const { ler, filtrar } = useEstadoDeLista();

  /*
   * A aba na URL, como no Acervo, no Cadastro Assistido e nos Cadastros.
   *
   * **O caso que a exige é o daqui:** revisar um comentário leva quem revisa à
   * aba de revisão, e voltar de uma questão ou de um recarregamento devolvia
   * "Leis no ar" - a aba de onde ninguém tinha saído. Vale igual para mandar o
   * link a alguém: "abre a Legislação e clica na terceira aba" é instrução que a
   * barra de endereço deveria dar sozinha.
   *
   * `revisao` era o nome antigo da terceira aba, quando ela só respondia "o que
   * falta revisar". O link velho continua chegando onde chegava.
   */
  const naUrl = ler.texto('aba');
  const aba = naUrl === 'revisao' ? 'comentarios' : (naUrl ?? ABA_PADRAO);

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={compacto ? 4 : 3} style={{ marginBottom: 4 }}>
          Legislação Comentada
        </Typography.Title>
        <Typography.Text type="secondary">
          A lei inteira para o aluno, comentada nos artigos que a prova cobra.
        </Typography.Text>
      </div>

      <Tabs
        activeKey={aba}
        onChange={(chave) => filtrar({ aba: chave === ABA_PADRAO ? undefined : chave })}
        items={[
          { key: 'leis', label: 'Leis no ar', children: <Leis /> },
          { key: 'backlog', label: 'O que comentar', children: <Backlog /> },
          { key: 'comentarios', label: 'Comentários', children: <Inventario /> },
        ]}
      />
    </Space>
  );
}

/* --------------------------------------------------------------- leis */

/** Da mais cobrada para a menos. É a ordem em que a decisão é tomada. */
const porPeso = (a: LeiDoAdmin, b: LeiDoAdmin) =>
  b.questoes - a.questoes || a.title.localeCompare(b.title);

function Leis() {
  const compacto = useCompacto();
  const navegar = useNavigate();
  const { message } = App.useApp();
  const clienteDeConsultas = useQueryClient();

  const leis = useQuery({ queryKey: ['admin-laws'], queryFn: () => api.listAdminLaws() });

  const publicar = useMutation({
    mutationFn: ({ id, publicada }: { id: string; publicada: boolean }) =>
      api.setLawPublication(id, publicada),
    onSuccess: async (_dados, variaveis) => {
      message.success(variaveis.publicada ? 'Lei publicada no app.' : 'Lei tirada do app.');
      await clienteDeConsultas.invalidateQueries({ queryKey: ['admin-laws'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  /**
   * Liberar os comentários da lei para todo mundo.
   *
   * **Não é o mesmo que publicar**, e as duas chaves ficam lado a lado por isso:
   * publicar põe o **texto** da lei no app, e o texto é domínio público, está no
   * Planalto; isto abre o **comentário**, que é o que se vende (D-42). Uma lei
   * pode estar no app e paga, no app e grátis, ou fora do app.
   */
  /**
   * As prateleiras da lei no catálogo do aluno.
   *
   * Sem elas, as leis publicadas chegam ao aluno numa parede única; com elas, o
   * catálogo vira estante. É trabalho de quem publica, e por isso mora na linha
   * ao lado das outras duas decisões da publicação.
   *
   * **Mais de uma por lei**, porque lei não respeita prateleira: a de Execuções
   * Fiscais é processual e tributária, e escolher uma das duas some com ela para
   * quem varre a outra.
   */
  const classificar = useMutation({
    mutationFn: ({ id, areas }: { id: string; areas: AreaDaLei[] }) => api.setLawAreas(id, areas),
    onSuccess: async (_dados, variaveis) => {
      message.success(
        variaveis.areas.length > 0
          ? `Lei em ${nomeDasAreas(variaveis.areas)}.`
          : 'Lei sem estante: cai em "Outras" no catálogo.',
      );
      await clienteDeConsultas.invalidateQueries({ queryKey: ['admin-laws'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const liberar = useMutation({
    mutationFn: ({ id, gratuita }: { id: string; gratuita: boolean }) =>
      api.setLawFree(id, gratuita),
    onSuccess: async (_dados, variaveis) => {
      message.success(
        variaveis.gratuita
          ? 'Comentários liberados para todo mundo.'
          : 'Comentários voltaram a exigir o módulo.',
      );
      await clienteDeConsultas.invalidateQueries({ queryKey: ['admin-laws'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  if (leis.isPending) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (leis.error) return <Alert type="error" showIcon title={(leis.error as Error).message} />;

  const chave = (lei: LeiDoAdmin) => ({
    checked: lei.publicada,
    loading: publicar.isPending && publicar.variables?.id === lei.id,
    onChange: (valor: boolean) => publicar.mutate({ id: lei.id, publicada: valor }),
  });

  /*
   * A chave da gratuidade só faz sentido sobre lei que está no ar: liberar de
   * graça o que ninguém pode abrir seria uma porta para lugar nenhum, e a
   * política recusa antes de olhar o direito.
   */
  const chaveDoGratis = (lei: LeiDoAdmin) => ({
    checked: lei.gratuita,
    disabled: !lei.publicada,
    loading: liberar.isPending && liberar.variables?.id === lei.id,
    onChange: (valor: boolean) => liberar.mutate({ id: lei.id, gratuita: valor }),
  });

  const abrir = (lei: LeiDoAdmin) => navegar(`/legislacao/leis/${lei.id}`);

  /*
   * A chave não pode levar para a lei junto: clicar em "publicar" é uma decisão,
   * e sair da tela no mesmo gesto tiraria da vista a confirmação do que se
   * acabou de fazer.
   */
  const semPropagar = (conteudo: React.ReactNode) => (
    <span
      onClick={(evento) => evento.stopPropagation()}
      onKeyDown={(evento) => evento.stopPropagation()}
      role="presentation"
    >
      {conteudo}
    </span>
  );

  const ordenadas = [...(leis.data ?? [])].sort(porPeso);

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        title="O aluno lê a lei inteira; o comentário é o que se vende"
        description="Publicar põe o texto oficial da lei no app para quem tem conta - o texto é domínio público. Cada lei é um produto: os comentários dela abrem para quem comprou algum produto que a conceda, e a chave Grátis os abre para todo mundo. A coluna Vendida em diz quem a vende hoje. Clique numa lei para vê-la artigo a artigo."
      />

      {compacto ? (
        <List<LeiDoAdmin>
          dataSource={ordenadas}
          pagination={paginacao(20)}
          renderItem={(lei) => (
            <List.Item
              style={{ paddingInline: 0, alignItems: 'flex-start', cursor: 'pointer' }}
              onClick={() => abrir(lei)}
            >
              <Flex vertical gap={6} style={{ width: '100%', minWidth: 0 }}>
                <Flex align="center" justify="space-between" gap={8}>
                  <Typography.Text code>{lei.citation}</Typography.Text>
                  {/* As duas chaves na primeira linha, rotuladas: sem o rótulo,
                      dois interruptores iguais lado a lado não dizem qual é qual. */}
                  <Flex align="center" gap={10}>
                    <Flex align="center" gap={4}>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        No app
                      </Typography.Text>
                      {semPropagar(<Switch size="small" {...chave(lei)} />)}
                    </Flex>
                    <Flex align="center" gap={4}>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Grátis
                      </Typography.Text>
                      {semPropagar(<Switch size="small" {...chaveDoGratis(lei)} />)}
                    </Flex>
                  </Flex>
                </Flex>

                <Typography.Text strong>{lei.title}</Typography.Text>

                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  <strong>{lei.questoes}</strong> questão(ões) na prova · {lei.artigosCitados} de{' '}
                  {lei.artigos} artigo(s) cobrado(s) · {lei.comentados} comentado(s)
                </Typography.Text>
              </Flex>
            </List.Item>
          )}
        />
      ) : (
        <Table<LeiDoAdmin>
          size="small"
          rowKey="id"
          dataSource={leis.data}
          pagination={paginacao(20)}
          scroll={{ x: somar(LARGURA_DAS_LEIS) + PISO_DO_TEXTO }}
          onRow={(lei) => ({ onClick: () => abrir(lei), style: { cursor: 'pointer' } })}
          columns={[
            {
              title: 'Lei',
              dataIndex: 'citation',
              width: LARGURA_DAS_LEIS.lei,
              sorter: (a, b) => a.citation.localeCompare(b.citation),
            },
            {
              title: 'Título',
              dataIndex: 'title',
              ellipsis: true,
              sorter: (a, b) => a.title.localeCompare(b.title),
            },
            {
              /*
               * **A coluna que decide qual lei comentar primeiro.** Conta cada
               * questão uma vez, ainda que ela cite oito artigos da mesma lei -
               * somar menções mediria quanto se escreveu, e não quanto a lei
               * pesa na prova. É a ordenação inicial da tabela.
               */
              title: 'Questões',
              dataIndex: 'questoes',
              width: LARGURA_DAS_LEIS.questoes,
              align: 'right',
              defaultSortOrder: 'descend',
              sorter: (a, b) => a.questoes - b.questoes,
              render: (valor: number) =>
                valor > 0 ? (
                  <Tag color="blue">{valor}</Tag>
                ) : (
                  <Typography.Text type="secondary">-</Typography.Text>
                ),
            },
            {
              /*
               * O par da coluna anterior: quantos artigos distintos a prova
               * cobra. Uma lei cobrada em 55 questões espalhadas por 35 artigos
               * fecha; a mesma contagem espalhada por 300 é outra empreitada.
               */
              title: 'Artigos cobrados',
              dataIndex: 'artigosCitados',
              width: LARGURA_DAS_LEIS.artigosCitados,
              align: 'right',
              sorter: (a, b) => a.artigosCitados - b.artigosCitados,
              render: (valor: number, linha) => (
                <Typography.Text type="secondary">
                  {valor} de {linha.artigos}
                </Typography.Text>
              ),
            },
            {
              title: 'Comentados',
              dataIndex: 'comentados',
              width: LARGURA_DAS_LEIS.comentados,
              align: 'right',
              sorter: (a, b) => a.comentados - b.comentados,
              render: (valor: number) => (valor > 0 ? <Tag color="success">{valor}</Tag> : '-'),
            },
            {
              title: 'No app',
              dataIndex: 'publicada',
              width: LARGURA_DAS_LEIS.publicada,
              sorter: (a, b) => Number(a.publicada) - Number(b.publicada),
              render: (_valor: boolean, linha) => semPropagar(<Switch {...chave(linha)} />),
            },
            {
              /*
               * Ao lado da "No app" de propósito: as duas decisões são vizinhas
               * e diferentes, e vê-las juntas é o que impede confundir publicar
               * o texto com liberar o comentário.
               */
              title: 'Grátis',
              dataIndex: 'gratuita',
              width: LARGURA_DAS_LEIS.gratis,
              sorter: (a, b) => Number(a.gratuita) - Number(b.gratuita),
              render: (_valor: boolean, linha) => semPropagar(<Switch {...chaveDoGratis(linha)} />),
            },
            {
              /*
               * A estante é a terceira decisão da publicação, e fica junto das
               * outras duas: publicar o texto, liberar o comentário, e dizer em
               * que prateleiras do catálogo a lei aparece.
               */
              title: 'Estantes',
              dataIndex: 'areas',
              width: LARGURA_DAS_LEIS.area,
              /*
               * Pela primeira estante, que é a de maior peso: ordenar pela
               * quantidade juntaria "Civil" com "Penal" só porque as duas leis
               * têm uma etiqueta, que não é o que se procura ao ordenar por
               * estante. As sem nenhuma caem no fim, com "Outras".
               */
              sorter: (a, b) => ordemDaArea(a.areas[0]) - ordemDaArea(b.areas[0]),
              render: (_valor: AreaDaLei[], linha) =>
                semPropagar(
                  <Select<AreaDaLei[]>
                    mode="multiple"
                    size="small"
                    variant="filled"
                    style={{ width: '100%' }}
                    // Uma etiqueta e o "+N" quando não couber: a linha da tabela
                    // tem altura fixa, e deixar as etiquetas quebrarem faria a
                    // lei de três estantes empurrar as vizinhas.
                    maxTagCount="responsive"
                    // "Outras" não é estante: é a ausência dela, e a tela do
                    // aluno junta essas leis no fim com esse nome.
                    placeholder="Outras"
                    allowClear
                    value={linha.areas}
                    loading={classificar.isPending && classificar.variables?.id === linha.id}
                    options={AREAS_DA_LEI.map(({ area, nome }) => ({ value: area, label: nome }))}
                    onChange={(areas) => classificar.mutate({ id: linha.id, areas: areas ?? [] })}
                  />,
                ),
            },
            {
              /**
               * **Quem vende esta lei** (G-NO-SILENT-LOCK).
               *
               * Desde a produtização, cada Legislação Comentada tem chave
               * própria e só abre por um produto que a conceda (D-44). O modo de
               * falhar é silencioso: a lei fica publicada, paga, e ninguém pode
               * ler o comentário dela - nem comprando -, sem que nada levante
               * erro. A coluna existe para isso não passar despercebido, e fica
               * na tela onde a lei se publica.
               */
              title: 'Vendida em',
              dataIndex: 'produtos',
              width: LARGURA_DAS_LEIS.produtos,
              render: (produtos: string[], linha) => {
                if (linha.gratuita) return <Typography.Text type="secondary">grátis</Typography.Text>;
                if (produtos.length > 0) {
                  return produtos.map((nome) => (
                    <Tag key={nome} color="processing">
                      {nome}
                    </Tag>
                  ));
                }
                // Não publicada ainda não é problema: ninguém a procura no app.
                return linha.publicada ? (
                  <Tooltip title="Publicada e paga, e nenhum produto à venda a concede: ninguém consegue ler os comentários dela, nem comprando.">
                    <Tag color="warning" icon={<WarningOutlined />}>
                      nenhum produto
                    </Tag>
                  </Tooltip>
                ) : (
                  <Typography.Text type="secondary">-</Typography.Text>
                );
              },
            },
            // A seta diz que a linha leva a algum lugar, sem depender de o
            // ponteiro virar mãozinha para alguém descobrir.
            {
              title: '',
              width: LARGURA_DAS_LEIS.seta,
              render: () => <RightOutlined style={{ opacity: 0.45 }} />,
            },
          ]}
        />
      )}
    </Space>
  );
}

/* ------------------------------------------------------------ backlog */

/** A chave de um artigo na seleção: a lei e o número, que é o par que o identifica. */
const chaveDoArtigo = (artigo: { legislationId: string; artigo: string }) =>
  `${artigo.legislationId}|${artigo.artigo}`;

const paraOEnvio = (artigo: ArtigoPendente) => ({
  legislationId: artigo.legislationId,
  article: artigo.artigo,
});

function Backlog() {
  const compacto = useCompacto();
  const { message } = App.useApp();
  const clienteDeConsultas = useQueryClient();
  const { ler, filtrar } = useEstadoDeLista();
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [pedindo, setPedindo] = useState<ArtigoPendente[] | null>(null);
  const [briefing, setBriefing] = useState('');

  // O filtro vive na URL, como o resto do estado de lista do admin - e é o mesmo
  // parâmetro do inventário, então trocar de aba não perde a lei escolhida. A
  // ordem também: as duas abas são a mesma sequência de trabalho.
  const lei = ler.texto('lei');
  const ordem = lerOpcao<OrdemDoBacklog>(ler.texto('ordem'), ORDENS_DO_BACKLOG);
  /*
   * **A varredura só existe com uma lei escolhida**, e o recorte cai sozinho
   * quando a lei sai: "toda a lei" sem lei é o acervo inteiro, e é o que a rota
   * recusa. Sem isto, limpar o seletor de lei deixaria a tela pedindo uma
   * consulta inválida com o `Segmented` já fora do campo de visão.
   */
  const recorte = lei
    ? lerOpcao<RecorteDoBacklog>(ler.texto('recorte'), RECORTES_DO_BACKLOG)
    : undefined;
  const varrendo = recorte === VARREDURA_DA_LEI;

  const leis = useQuery({ queryKey: ['admin-laws'], queryFn: () => api.listAdminLaws() });
  const backlog = useQuery({
    /*
     * **Sem `limite`: a lista chega inteira, e quem pagina é o navegador.** Ela
     * pedia 200 e o servidor cortava aí - a Lei de Registros Públicos parava no
     * art. 296, o Código Civil no 1.596, e a única pista era o "200 no total" do
     * rodapé, que ninguém lê como aviso. E a ordem vai junto, ao servidor: com o
     * recorte da lei inteira são dois mil artigos, e ordená-los na tabela seria
     * ordenar de novo o que já veio ordenado.
     */
    queryKey: ['article-backlog', lei, ordem, recorte],
    queryFn: () => api.articleBacklog({ lei, ordem, recorte }),
  });

  const lista = backlog.data ?? [];
  const porChave = new Map(lista.map((item) => [chaveDoArtigo(item), item]));
  const escolhidos = selecionados
    .map((chave) => porChave.get(chave))
    .filter((item): item is ArtigoPendente => item !== undefined);

  const alternar = (chave: string, marcado: boolean) =>
    setSelecionados((atuais) =>
      marcado ? [...atuais, chave] : atuais.filter((outra) => outra !== chave),
    );

  const limpar = async () => {
    setSelecionados([]);
    setPedindo(null);
    setBriefing('');
    await clienteDeConsultas.invalidateQueries({ queryKey: ['article-backlog'] });
  };

  const pedir = useMutation({
    mutationFn: (dados: { artigos: ArtigoPendente[]; briefing?: string }) =>
      api.requestArticleCommentary({
        artigos: dados.artigos.map(paraOEnvio),
        ...(dados.briefing ? { briefing: dados.briefing } : {}),
      }),
    onSuccess: async (resultado) => {
      /*
       * **O que ficou de fora é dito, e não engolido.** No lote sempre haverá
       * artigo já publicado ou já com um motor, e uma mensagem de sucesso limpa
       * faria quem pediu acreditar que os cinquenta entraram.
       */
      const entraram = resultado.pedidos.length;
      const fora = resultado.recusados.length;
      if (entraram === 0) {
        message.warning(`Nenhum artigo entrou: ${fora} já está com um motor ou publicado.`);
      } else {
        message.success(
          fora === 0
            ? `${entraram} artigo(s) na lista. O motor os pega nas próximas chamadas.`
            : `${entraram} artigo(s) na lista; ${fora} ficou(ram) de fora, já com um motor ou publicado(s).`,
        );
      }
      await limpar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const cancelar = useMutation({
    mutationFn: (artigos: ArtigoPendente[]) => api.cancelArticleRequest(artigos.map(paraOEnvio)),
    onSuccess: async (resultado) => {
      const desfeitos = resultado.cancelados.length;
      /*
       * Quantos voltaram a ser sugestão importa: o pedido de retrabalho incide
       * sobre artigo que já tem comentário, e o texto continua lá.
       */
      const comTexto = resultado.cancelados.filter((c) => c.devolvidoA === 'sugerido').length;
      if (desfeitos === 0) {
        message.warning('Nada a desfazer: nenhum dos escolhidos estava pedido.');
      } else {
        message.success(
          comTexto === 0
            ? `${desfeitos} pedido(s) desfeito(s).`
            : `${desfeitos} pedido(s) desfeito(s); ${comTexto} voltou(aram) a ser sugestão, com o comentário intacto.`,
        );
      }
      await limpar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  /** Só o que está pedido tem o que desfazer. */
  const pedidosEscolhidos = escolhidos.filter((item) => item.estado === 'pendente');

  const acao = (linha: ArtigoPendente) =>
    linha.estado === 'pendente' ? (
      <Button size="small" loading={cancelar.isPending} onClick={() => cancelar.mutate([linha])}>
        Remover pedido
      </Button>
    ) : (
      <Button size="small" onClick={() => setPedindo([linha])}>
        Pedir comentário
      </Button>
    );

  /**
   * Marcar a lista inteira, e não a página.
   *
   * **É o que faltava para "pedir a lei toda".** A caixa do cabeçalho do Ant
   * Design marca as linhas da página - vinte -, e com a lei inteira na tela são
   * dezesseis páginas de cliques para chegar aos 321 artigos da Lei de Registros
   * Públicos. A lista chega inteira ao navegador, então marcar tudo é marcar o
   * que está aqui, e não uma promessa sobre o que viria na próxima requisição.
   *
   * Fica junto da legenda, e não na barra de seleção: a barra só existe depois
   * do primeiro clique, e é justamente esse clique que o botão poupa.
   */
  const todosMarcados = lista.length > 0 && escolhidos.length === lista.length;
  const marcarTodos = () => setSelecionados(lista.map(chaveDoArtigo));

  /**
   * A barra que aparece com a seleção.
   *
   * Mesma forma da lista de Questões: a contagem à esquerda, as ações à direita,
   * e ela some quando não há seleção - barra sempre visível vira moldura que
   * ninguém lê.
   *
   * **Conta o que a lista ainda tem**, e não as chaves guardadas: trocar de lei
   * com linhas marcadas deixa chaves que não apontam para nada, e elas faziam a
   * barra abrir dizendo "0 selecionado(s)" sobre a lista nova.
   */
  const barra =
    escolhidos.length === 0 ? null : (
      <Flex align="center" gap={8} wrap>
        <Typography.Text strong>{escolhidos.length} selecionado(s)</Typography.Text>
        <Button
          size="small"
          type="primary"
          disabled={escolhidos.length === 0}
          onClick={() => setPedindo(escolhidos)}
        >
          Pedir comentário
        </Button>
        <Button
          size="small"
          loading={cancelar.isPending}
          disabled={pedidosEscolhidos.length === 0}
          onClick={() => cancelar.mutate(pedidosEscolhidos)}
        >
          Remover pedido{pedidosEscolhidos.length > 0 ? ` (${pedidosEscolhidos.length})` : ''}
        </Button>
        <Button size="small" type="text" onClick={() => setSelecionados([])}>
          Limpar
        </Button>
      </Flex>
    );

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Flex vertical={compacto} align={compacto ? 'stretch' : 'center'} gap={8}>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          // No celular ocupa a largura toda: um select de 260px ao lado de um
          // texto empurra a tela para fora do viewport.
          style={compacto ? { width: '100%' } : { minWidth: 280 }}
          placeholder="Todas as leis"
          value={lei}
          onChange={(valor) => filtrar({ lei: valor })}
          options={[...(leis.data ?? [])].sort(porPeso).map((item) => ({
            value: item.citation,
            label: `${item.citation} · ${item.title}`,
          }))}
        />
        {/*
          O seletor, e não só o cabeçalho da tabela: abaixo de `md` a lista não
          tem cabeçalho nenhum, e a ordem ficaria fora do alcance de quem abre a
          tela no celular.
        */}
        <Select<OrdemDoBacklog>
          value={ordem ?? 'incidencia'}
          onChange={(valor) => filtrar({ ordem: valor === 'incidencia' ? undefined : valor })}
          style={compacto ? { width: '100%' } : { minWidth: 220 }}
          options={ORDENS_DO_BACKLOG}
        />
        {/*
          O recorte só aparece com uma lei escolhida, porque é sobre uma lei que
          ele fala: "toda a lei" sem lei não quer dizer nada, e um controle
          desabilitado ao lado do seletor faria a pergunta parecer respondível.
        */}
        {lei ? (
          <Segmented<RecorteDoBacklog>
            value={recorte ?? 'cobrados'}
            onChange={(valor) =>
              filtrar({ recorte: valor === 'cobrados' ? undefined : valor })
            }
            block={compacto}
            options={RECORTES_DO_BACKLOG}
          />
        ) : null}
      </Flex>

      {/* A legenda diz o que a lista está fazendo, então acompanha as escolhas:
          explicar a incidência sobre uma lista ordenada pela lei seria mentir
          com cara de ajuda. */}
      <Flex align="center" gap={8} wrap>
        <Typography.Text type="secondary" style={{ fontSize: compacto ? 12 : undefined }}>
          {varrendo
            ? 'A lei inteira, artigo por artigo - inclusive o que prova nenhuma cobrou ainda.'
            : ordem === ORDEM_DA_LEI
              ? 'Na ordem da lei, do primeiro artigo ao último - e a contagem de questões continua à vista.'
              : 'A ordem é a incidência: quantas questões publicadas cobram o artigo.'}
        </Typography.Text>
        {lista.length > 0 && !todosMarcados ? (
          <Button size="small" type="link" style={{ padding: 0 }} onClick={marcarTodos}>
            Marcar os {lista.length}
          </Button>
        ) : null}
      </Flex>

      {barra}

      {backlog.isPending ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : lista.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nada esperando comentário com esse filtro."
        />
      ) : compacto ? (
        <List<ArtigoPendente>
          dataSource={lista}
          pagination={paginacao(20)}
          renderItem={(linha) => (
            <List.Item style={{ paddingInline: 0, alignItems: 'flex-start' }}>
              {/*
                A caixa à esquerda do cartão, dentro de um `Flex` - como na lista
                de Questões. Solta como irmã do conteúdo, o `List.Item` a
                distribui com `space-between` e ela vai parar do outro lado.
              */}
              <Flex gap={10} style={{ width: '100%', minWidth: 0 }}>
                <Checkbox
                  checked={selecionados.includes(chaveDoArtigo(linha))}
                  onChange={(evento) => alternar(chaveDoArtigo(linha), evento.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <Flex vertical gap={6} style={{ minWidth: 0, flex: 1 }}>
                  <Flex align="center" gap={8} wrap>
                    <Typography.Text strong>art. {linha.artigo}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {linha.lei}
                    </Typography.Text>
                    {/* Sem etiqueta quando ninguém cobra o artigo: a situação
                        ao lado já diz o que a linha é, e "0 questão(ões)" em
                        cada cartão de uma varredura vira ruído. */}
                    {linha.incidencia > 0 ? (
                      <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                        {linha.incidencia} questão(ões)
                      </Tag>
                    ) : null}
                    {linha.estado ? (
                      <Tag color={ROTULO[linha.estado].cor} style={{ marginInlineEnd: 0 }}>
                        {ROTULO[linha.estado].texto}
                      </Tag>
                    ) : null}
                  </Flex>

                  {linha.briefing ? (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {linha.briefing}
                    </Typography.Text>
                  ) : null}

                  {/* A ação vira a última linha do cartão: numa tabela de seis
                      colunas seria a primeira coisa a sair do campo de visão. */}
                  {acao(linha)}
                </Flex>
              </Flex>
            </List.Item>
          )}
        />
      ) : (
        <Table<ArtigoPendente>
          size="small"
          rowKey={(linha) => chaveDoArtigo(linha)}
          dataSource={lista}
          rowSelection={{
            selectedRowKeys: selecionados,
            onChange: (chaves) => setSelecionados(chaves as string[]),
          }}
          pagination={paginacao(20)}
          scroll={{ x: somar(LARGURA_DO_BACKLOG) + LARGURA_DA_SELECAO + PISO_DO_TEXTO }}
          /* A paginação daqui é do navegador e também passa por este callback:
             sem o `action`, virar a página desfaria a ordenação escolhida. */
          onChange={(_pagina, _filtros, ordenacao, extra) => {
            if (extra.action !== 'sort') return;
            filtrar({ ordem: ordemDoCabecalho(ordenacao) });
          }}
          columns={[
            { title: 'Lei', dataIndex: 'lei', width: LARGURA_DO_BACKLOG.lei },
            colunaDoArtigo<ArtigoPendente>(LARGURA_DO_BACKLOG.artigo, ordem),
            {
              title: 'Questões',
              dataIndex: 'incidencia',
              width: LARGURA_DO_BACKLOG.questoes,
              align: 'right',
              /*
               * **Zero é zero, e quem diz "pedido" é a coluna da situação.** O
               * zero aqui só existia no artigo pedido à mão, e a coluna
               * aproveitava para dizer por que a linha estava na lista. Com a
               * lei inteira à vista, zero passou a ser o caso comum - e a coluna
               * chamava de pedido cada um dos 221 artigos que ninguém pediu.
               */
              render: (valor: number) =>
                valor > 0 ? (
                  <Tag color="blue">{valor}</Tag>
                ) : (
                  <Typography.Text type="secondary">-</Typography.Text>
                ),
            },
            {
              title: 'Situação',
              dataIndex: 'estado',
              width: LARGURA_DO_BACKLOG.situacao,
              render: (estado: EstadoDoArtigo | null) =>
                estado ? <Tag color={ROTULO[estado].cor}>{ROTULO[estado].texto}</Tag> : '-',
            },
            { title: 'Ressalva', dataIndex: 'briefing', ellipsis: true },
            {
              title: '',
              width: LARGURA_DO_BACKLOG.acao,
              /* Presa à direita pelo mesmo motivo da tela da lei: é a coluna
                 que se clica, e é a primeira a sair pela direita quando a
                 janela é a menor que ainda mostra a tabela. */
              fixed: 'right',
              render: (_valor, linha) => acao(linha),
            },
          ]}
        />
      )}

      <Modal
        open={pedindo !== null}
        title={
          pedindo?.length === 1
            ? `Pedir comentário do art. ${pedindo[0]!.artigo} da ${pedindo[0]!.lei}`
            : `Pedir comentário de ${pedindo?.length ?? 0} artigos`
        }
        okText="Pedir"
        confirmLoading={pedir.isPending}
        onCancel={() => {
          setPedindo(null);
          setBriefing('');
        }}
        onOk={() =>
          pedindo &&
          pedir.mutate({
            artigos: pedindo,
            ...(briefing.trim() ? { briefing: briefing.trim() } : {}),
          })
        }
      >
        <Space orientation="vertical" size={8} style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            A ressalva chega a quem for escrever, e vale para a leva inteira. Use-a quando já houve
            uma tentativa e ela não serviu - dizer o que faltou evita que a próxima repita a
            anterior.
          </Typography.Text>
          <Input.TextArea
            rows={4}
            value={briefing}
            placeholder="Opcional. Ex.: faltou dizer o que muda na prática depois da Lei 14.711/2023."
            onChange={(evento) => setBriefing(evento.target.value)}
          />
          {/*
            O que já está ocupado é dito antes de pedir, e não só depois: quem
            selecionou a lei inteira merece saber que dois artigos vão ficar de
            fora antes de clicar.
          */}
          {(pedindo ?? []).some((item) => item.estado === 'reivindicado') ? (
            <Typography.Text type="warning" style={{ fontSize: 12 }}>
              {(pedindo ?? []).filter((item) => item.estado === 'reivindicado').length} artigo(s) da
              seleção estão com um motor agora e vão ficar de fora.
            </Typography.Text>
          ) : null}
        </Space>
      </Modal>
    </Space>
  );
}

/* --------------------------------------------------------- inventário */

/**
 * Tudo o que já se comentou, em qualquer estado.
 *
 * **A aba nasceu como "Revisar" e era pouco.** Filtrava por sugestão, e o que
 * estava publicado - justamente o que o aluno lê - ficava atrás de um filtro que
 * ninguém tinha motivo para mexer. Corrigir um comentário depois de publicado é
 * a operação que mais vai acontecer com o tempo, e ela começa por achá-lo.
 */
function Inventario() {
  const compacto = useCompacto();
  const telas = Grid.useBreakpoint();
  const { filtrar, irParaPagina, busca } = useEstadoDeLista();
  const [aberto, setAberto] = useState<ArtigoNaGaveta | null>(null);

  /*
   * O `Segmented` diz o formato do inventário de uma vez, mas com as contagens
   * ele passa de 700px. Abaixo de `lg` viraria uma fila cortada na borda - e o
   * `Select` mostra os seis.
   */
  const estadoEmLista = !telas.lg;

  const consulta = lerConsulta(new URLSearchParams(busca));
  const { estado, ordem, page, porPagina } = consulta;

  const leis = useQuery({ queryKey: ['admin-laws'], queryFn: () => api.listAdminLaws() });
  const resumo = useQuery(resumoDoInventario(consulta));
  const lista = useQuery(paginaDoInventario(consulta));

  const opcoes = opcoesDeEstado(resumo.data, rotuloDoEstado);
  const trocarEstado = (valor: EstadoDoArtigo | 'todos') =>
    filtrar({ estado: valor === 'todos' ? undefined : valor });

  const abrir = (linha: ArtigoComentado) =>
    setAberto({ id: linha.id, lei: linha.lei, artigo: linha.artigo });

  const vazio = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        consulta.termo ? `Nada com "${consulta.termo}".` : 'Nenhum comentário com esse recorte.'
      }
    >
      {/* A saída natural de quem procurou e não achou: pedir que se escreva. */}
      <Button onClick={() => filtrar({ aba: 'backlog', estado: undefined, termo: undefined })}>
        Ver o que falta comentar
      </Button>
    </Empty>
  );

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      {estadoEmLista ? (
        <Select<EstadoDoArtigo | 'todos'>
          value={estado ?? 'todos'}
          onChange={trocarEstado}
          style={{ width: '100%' }}
          options={opcoes.map((opcao) => ({
            value: opcao.value,
            label: opcao.count === undefined ? opcao.label : `${opcao.label} (${opcao.count})`,
          }))}
        />
      ) : (
        <Segmented<EstadoDoArtigo | 'todos'>
          block
          value={estado ?? 'todos'}
          onChange={trocarEstado}
          options={opcoes.map((opcao) => ({
            value: opcao.value,
            /*
             * O número embaixo do rótulo, e não ao lado: com `block`, o que
             * decide a largura de todos é o pedaço mais longo, e "Com um motor
             * 12" lado a lado estoura a linha num monitor comum.
             */
            label: (
              <Flex vertical align="center" style={{ minWidth: 0, lineHeight: 1.35 }}>
                <span
                  style={{
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {opcao.label}
                </span>
                {opcao.count === undefined ? null : (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {opcao.count}
                  </Typography.Text>
                )}
              </Flex>
            ),
            tooltip: opcao.label,
          }))}
        />
      )}

      <Flex vertical={compacto} gap={8} align={compacto ? 'stretch' : 'center'} wrap>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          style={compacto ? { width: '100%' } : { minWidth: 260 }}
          placeholder="Todas as leis"
          value={consulta.lei}
          onChange={(valor) => filtrar({ lei: valor })}
          options={[...(leis.data ?? [])].sort(porPeso).map((item) => ({
            value: item.citation,
            label: `${item.citation} · ${item.title}`,
          }))}
        />
        {/*
          A busca alcança o **texto do comentário**, e não só o número do artigo:
          a pergunta que se faz de verdade é "onde eu escrevi sobre isso".
        */}
        <Input.Search
          allowClear
          defaultValue={consulta.termo}
          style={compacto ? { width: '100%' } : { maxWidth: 320 }}
          placeholder="Artigo, lei ou palavra do comentário"
          onSearch={(valor) => filtrar({ termo: valor.trim() || undefined })}
        />
        <Select
          value={ordem ?? 'recentes'}
          onChange={(valor) => filtrar({ ordem: valor === 'recentes' ? undefined : valor })}
          style={compacto ? { width: '100%' } : { minWidth: 220 }}
          options={ORDENS}
        />
      </Flex>

      {lista.isPending ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (lista.data?.items ?? []).length === 0 ? (
        vazio
      ) : compacto ? (
        <List<ArtigoComentado>
          dataSource={lista.data?.items}
          pagination={false}
          renderItem={(linha) => (
            <List.Item
              style={{ paddingInline: 0, alignItems: 'flex-start', cursor: 'pointer' }}
              onClick={() => abrir(linha)}
            >
              <Flex vertical gap={6} style={{ width: '100%', minWidth: 0 }}>
                <Flex align="center" gap={8} wrap>
                  <Typography.Text strong>art. {linha.artigo}</Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {linha.lei}
                  </Typography.Text>
                  <Tag color={ROTULO[linha.state].cor} style={{ marginInlineEnd: 0 }}>
                    {ROTULO[linha.state].texto}
                  </Tag>
                  {linha.incidencia > 0 ? (
                    <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                      {linha.incidencia} questão(ões)
                    </Tag>
                  ) : null}
                </Flex>

                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {formatarData(linha.updatedAt)} · escrito por{' '}
                  {linha.authorName ?? (linha.runId ? 'Comentário Assistido' : '-')}
                </Typography.Text>

                {linha.engineNote ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {linha.engineNote}
                  </Typography.Text>
                ) : null}
              </Flex>
            </List.Item>
          )}
        />
      ) : (
        <Table<ArtigoComentado>
          size="small"
          rowKey="id"
          dataSource={lista.data?.items}
          pagination={false}
          scroll={{ x: somar(LARGURA_DO_INVENTARIO) + PISO_DO_TEXTO }}
          onRow={(linha) => ({ onClick: () => abrir(linha), style: { cursor: 'pointer' } })}
          onChange={(_pagina, _filtros, ordenacao, extra) => {
            if (extra.action !== 'sort') return;
            filtrar({ ordem: ordemDoCabecalho(ordenacao) });
          }}
          columns={[
            { title: 'Lei', dataIndex: 'lei', width: LARGURA_DO_INVENTARIO.lei },
            colunaDoArtigo<ArtigoComentado>(LARGURA_DO_INVENTARIO.artigo, ordem),
            {
              title: 'Situação',
              dataIndex: 'state',
              width: LARGURA_DO_INVENTARIO.situacao,
              render: (valor: EstadoDoArtigo) => (
                <Tag color={ROTULO[valor].cor}>{ROTULO[valor].texto}</Tag>
              ),
            },
            {
              title: 'Questões',
              dataIndex: 'incidencia',
              width: LARGURA_DO_INVENTARIO.questoes,
              align: 'right',
            },
            {
              /*
               * **Autor nulo é a máquina** (D-17): o texto é o que ela sugeriu e
               * ninguém editou. Dizer "-" aqui esconderia justamente o que a
               * revisão precisa saber.
               */
              title: 'Escrito por',
              dataIndex: 'authorName',
              width: LARGURA_DO_INVENTARIO.autor,
              render: (nome: string | null, linha) =>
                nome ?? (linha.runId ? <Tag>Comentário Assistido</Tag> : '-'),
            },
            {
              // A data existe porque a ordem "parados há mais tempo" precisa ser
              // conferível: ordenação sem o número ordenado é ato de fé.
              title: 'Mexido em',
              dataIndex: 'updatedAt',
              width: LARGURA_DO_INVENTARIO.data,
              render: (valor: string) => formatarData(valor),
            },
            { title: 'Observação do motor', dataIndex: 'engineNote', ellipsis: true },
          ]}
        />
      )}

      {/*
        A paginação fora da tabela, servindo aos dois desenhos. Sem
        `hideOnSinglePage`: escondê-la levaria junto o seletor de tamanho, e quem
        tivesse posto 100 por página numa lista de 30 perderia o controle para
        voltar a 20.
      */}
      <Flex justify={compacto ? 'center' : 'flex-end'}>
        <Pagination
          current={page}
          pageSize={lista.data?.pageSize ?? porPagina}
          total={lista.data?.total ?? 0}
          size={compacto ? 'small' : undefined}
          showSizeChanger
          pageSizeOptions={TAMANHOS_DE_PAGINA}
          showTotal={(total) => `${total} comentário(s)`}
          /*
           * Uma escrita por interação: na troca de tamanho o Ant Design dispara
           * os dois callbacks, e a segunda escrita - que só conhece a página -
           * apagaria o tamanho novo.
           */
          onChange={(proxima, tamanho) => {
            if (tamanho !== porPagina) {
              filtrar({ porPagina: tamanho === POR_PAGINA_PADRAO ? undefined : tamanho });
              return;
            }
            irParaPagina(proxima);
          }}
        />
      </Flex>

      <GavetaDoComentario artigo={aberto} onClose={() => setAberto(null)} />
    </Space>
  );
}
